import React from "react";
import Select from "react-select";
import PropTypes from "prop-types";
import IndustryTypeSelect from "../../pages/pricing-plan/IndustryTypeSelect";

import {
  getAccountingSoftwareOptions,
  getCountryOptions,
  getOrgTypeOptions,
  getOwnPropertyOptions,
  getSingleUseOptions
} from "../../utils/getDropdownOptions";

const optionMapper = {
  accountingSoftware: getAccountingSoftwareOptions,
  country: getCountryOptions,
  orgType: getOrgTypeOptions,
  ownership: getOwnPropertyOptions,
  yesNo: getSingleUseOptions
};

// isRiskBased is stored as a boolean but the <select> needs its string form
const getSelectValue = (field, value) => {
  if (field.name === "isRiskBased") {
    return String(value === true);
  }
  return value !== undefined && value !== null ? String(value) : "";
};

export default function PricingPlanForm({
  field,
  formData = {},
  errors = {},
  onChange,
  handleMultiSelect
}) {
  const label = field.label || field.name;
  const value = formData?.[field.name];
  const error = errors?.[field.name];

  const getOptions = () =>
    typeof field.options === "string"
      ? optionMapper[field.options]?.() || []
      : field.options || [];

  const renderRow = (label, content, isSub = false) => (
    <div className={`row mb-2 ${isSub ? "mx-0 px-0" : ""}`}>
      <div className={`col-sm-4 ${isSub ? "ps-4" : ""}`}>
        <div className="input-title">
          <label>{label}</label>
        </div>
      </div>
      <div className="col-sm-8">
        <div className="input-group">{content}</div>
      </div>
    </div>
  );

  const renderInput = (f, val, err) => (
    <>
      <input
        name={f.name}
        type={f.inputType || "text"}
        value={val ?? ""}
        onChange={onChange}
        disabled={f.disabled}
        {...(f.placeholder && { placeholder: f.placeholder })}
        {...(f.min !== undefined && { min: f.min })}
        {...(f.step && { step: f.step })}
      />
      {err && <div className="text-danger small">{err}</div>}
    </>
  );

  switch (field.type) {
    case "input":
      return renderRow(label, renderInput(field, value, error));

    case "group":
      return (
        <div className="row mb-2">
          <div className="col-sm-12 mb-2">
            <div className="input-title">
              <label>{field.label}</label>
            </div>
          </div>

          {field.fields?.map((subField) => (
            <React.Fragment key={subField.name}>
              {renderRow(
                subField.label,
                renderInput(
                  subField,
                  formData?.[subField.name],
                  errors?.[subField.name]
                ),
                true
              )}
            </React.Fragment>
          ))}
        </div>
      );

    case "select":
      return renderRow(
        label,
        <>
          <select
            name={field.name}
            value={getSelectValue(field, value)}
            onChange={(e) => {
              if (field.name === "isRiskBased") {
                onChange({
                  target: {
                    name: field.name,
                    value: e.target.value === "true"
                  }
                });
              } else if (field.name === "primaryPricingPlanId") {
                onChange({
                  target: {
                    name: field.name,
                    value: e.target.value ? Number(e.target.value) : ""
                  }
                });
              } else {
                onChange(e);
              }
            }}
            disabled={field.disabled}
          >
            {getOptions().map((opt) => (
              <option key={opt.value} value={String(opt.value)}>
                {opt.label}
              </option>
            ))}
          </select>
          {error && <div className="text-danger small">{error}</div>}
        </>
      );

    case "multiSelect":
      return renderRow(
        label,
        <>
          <Select
            isMulti
            value={value || []}
            options={getOptions()}
            onChange={(val) =>
              handleMultiSelect && handleMultiSelect(field.name, val)
            }
            className="basic-multi-select"
            classNamePrefix="select"
          />
          {error && <div className="text-danger small">{error}</div>}
        </>
      );

    case "radio":
      return renderRow(
        label,
        <div className="row element_gap">
          {field.options?.map((opt, index) => {
            let isChecked;

            if (typeof value === "boolean") {
              isChecked =
                (value === true && index === 0) ||
                (value === false && index === 1);
            } else {
              isChecked = value === opt.value;
            }

            return (
              <div key={opt.value} className="col-md-12">
                <input
                  id={opt.value}
                  type="radio"
                  name={field.name}
                  value={opt.value}
                  checked={isChecked}
                  onChange={onChange}
                  disabled={field.disabled}
                />
                <label className="ms-1" htmlFor={opt.value}>
                  {opt.label}
                </label>
              </div>
            );
          })}
        </div>
      );

    case "industry":
      return renderRow(
        label,
        <>
          <IndustryTypeSelect
            name={field.name}
            value={value}
            onChange={onChange}
            disabled={field.disabled}
          />
          {error && <div className="text-danger small">{error}</div>}
        </>
      );

    default:
      return null;
  }
}

PricingPlanForm.propTypes = {
  field: PropTypes.object,
  formData: PropTypes.object,
  errors: PropTypes.object,
  onChange: PropTypes.func,
  handleMultiSelect: PropTypes.func
};
